import { Analytics } from '@/utils/analytics';
import { apolloClient } from '@/utils/apollo-client';
import {
  BackgroundSyncMetadata,
  OfflineAuthAdapter,
  OfflineBackgroundSyncRegistryAdapter,
  OfflineBatchTagPrintingAdapter,
  OfflineCreditRequestDataAdapter,
  OfflineDataStatus,
  OfflineDataStatusAdapter,
  OfflineDataStatusMap,
  OfflineOrderConstraintsAdapter,
  OfflineOrderContextAdapter,
  OfflineOrderGuideAdapter,
  OfflineOrderGuidePriceEditDisplayInfoAdapter,
  OfflineUserBackgroundSyncRegistryAdapter,
  OfflineUserContextAdapter,
  OfflineVariantPackInfoAdapter,
  OfflineVisibleVariantPackAdapter,
} from '@/adapters/offline-mode';
import { captureException } from '@/utils/sentry';
import {
  getExpiredSyncTypesToUpdate,
  MAX_CONCURRENT_ACCOUNT_DOWNLOADS,
} from '@/contexts/offline-download-management-context';
import { gql } from '@apollo/client';
import { isOfflineAuthTokenValid } from '@/utils/offline-mode/offline-mode-validation';
import { OfflineSyncType } from '@/utils/offline-mode/types';

export const SYNC_OFFLINE_DATA_BACKGROUND_TASK_ID = 'offline-background-sync';

const syncOfflineData = async (
  metadata: BackgroundSyncMetadata,
  restaurantUUID: string,
  supplierUUID: string,
): Promise<OfflineSyncType | void> => {
  try {
    let adapter;
    switch (metadata.syncType) {
      case OfflineSyncType.BatchTagPrinting:
        adapter = OfflineBatchTagPrintingAdapter.getInstance(
          restaurantUUID,
          supplierUUID,
        );
        break;
      case OfflineSyncType.CreditRequest:
        adapter = new OfflineCreditRequestDataAdapter(
          restaurantUUID,
          supplierUUID,
        );
        break;
      case OfflineSyncType.OrderConstraints:
        adapter = new OfflineOrderConstraintsAdapter(
          restaurantUUID,
          supplierUUID,
        );
        break;
      case OfflineSyncType.OrderContext:
        adapter = OfflineOrderContextAdapter.getInstance(
          restaurantUUID,
          supplierUUID,
        );
        break;
      case OfflineSyncType.OrderGuide:
        adapter = new OfflineOrderGuideAdapter(restaurantUUID, supplierUUID);
        break;
      case OfflineSyncType.PriceEditDisplayInfo:
        adapter = new OfflineOrderGuidePriceEditDisplayInfoAdapter(
          restaurantUUID,
          supplierUUID,
        );
        break;
      // User context syncs are handled through the userBackgroundSyncRegistry
      case OfflineSyncType.UserContext:
        break;
      case OfflineSyncType.VariantPackInfo:
        adapter = OfflineVariantPackInfoAdapter.getInstance(
          restaurantUUID,
          supplierUUID,
        );
        break;
      case OfflineSyncType.VisibleVariantPacks:
        adapter = new OfflineVisibleVariantPackAdapter(
          restaurantUUID,
          supplierUUID,
        );
        break;
      // Minimal variant pack data will be synced manually by user in scanner screen
      case OfflineSyncType.MinimalVariantPack:
        break;
      default:
        const _exhaustive: never = metadata.syncType;
        return _exhaustive;
    }

    if (adapter) {
      const { data } = await apolloClient.query({
        fetchPolicy: 'network-only',
        query: gql(metadata.query),
        variables: metadata.variables,
      });

      await adapter.save(data);
      return metadata.syncType;
    }
  } catch (e) {
    captureException(e);
  }
};

export const backgroundSyncOfflineData = async () => {
  const dataStatusAdapter = new OfflineDataStatusAdapter();
  const dataStatus = await dataStatusAdapter.fetch();
  const offlineAuthAdapter = new OfflineAuthAdapter();
  const offlineAuthToken = await offlineAuthAdapter.fetch();

  if (
    !dataStatus ||
    !offlineAuthToken ||
    !isOfflineAuthTokenValid(offlineAuthToken)
  ) {
    return;
  }

  const nowISOString = new Date().toISOString();
  const restaurantUUIDs = Object.keys(dataStatus);

  const backgroundSyncRegistryAdapter =
    new OfflineBackgroundSyncRegistryAdapter();
  const backgroundSyncRegistry = await backgroundSyncRegistryAdapter.fetch();
  const userBackgroundSyncRegistryAdapter =
    new OfflineUserBackgroundSyncRegistryAdapter();
  const userBackgroundSyncRegistry =
    await userBackgroundSyncRegistryAdapter.fetch();

  // Determine sync types to update for each restaurant
  const expiredSyncTypeMap = restaurantUUIDs.reduce<
    Record<string, Set<OfflineSyncType>>
  >((acc, restaurantUUID) => {
    acc[restaurantUUID] = new Set(
      getExpiredSyncTypesToUpdate(dataStatus[restaurantUUID]),
    );
    return acc;
  }, {});

  const updatedDataStatusMap: OfflineDataStatusMap = {
    ...dataStatus,
  };

  Analytics.track('offline_background_sync_started', {
    expiredRestaurantCount: Object.values(expiredSyncTypeMap).filter(
      (set) => set.size > 0,
    ).length,
    restaurantCount: restaurantUUIDs.length,
  });

  // Update user specific data syncs across all restaurants
  if (
    userBackgroundSyncRegistry &&
    restaurantUUIDs.some((restaurantUUID) =>
      expiredSyncTypeMap[restaurantUUID]?.has(OfflineSyncType.UserContext),
    )
  ) {
    for (const [userUUID, metadata] of Object.entries(
      userBackgroundSyncRegistry,
    )) {
      try {
        const { data } = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: gql(metadata.query),
          variables: metadata.variables,
        });

        const userContextAdapter = new OfflineUserContextAdapter(userUUID);
        await userContextAdapter.save(data);

        for (const restaurantUUID of restaurantUUIDs) {
          updatedDataStatusMap[restaurantUUID] = {
            ...(updatedDataStatusMap[restaurantUUID] ?? {}),
            [OfflineSyncType.UserContext]: {
              downloadedDate: nowISOString,
            },
          };
        }

        await dataStatusAdapter.save(updatedDataStatusMap);
      } catch (e) {
        captureException(e);
      }
    }
  }

  // Update individual offline data syncs for each restaurant in chunks
  if (backgroundSyncRegistry) {
    const syncRegistryEntries = Object.entries(backgroundSyncRegistry);

    let i = 0;
    while (i < syncRegistryEntries.length) {
      const chunk = syncRegistryEntries.slice(
        i,
        i + MAX_CONCURRENT_ACCOUNT_DOWNLOADS,
      );

      const syncRestaurant = async ([syncRegistryKey, syncData]: [
        string,
        Record<string, BackgroundSyncMetadata>,
      ]): Promise<[string, OfflineDataStatus]> => {
        const [restaurantUUID, supplierUUID] = syncRegistryKey.split(':');
        const expired = expiredSyncTypeMap[restaurantUUID] || new Set();
        const currentRestaurantDataStatus = dataStatus[restaurantUUID] ?? {};

        const updatedDataStatus: OfflineDataStatus =
          'downloadedDate' in currentRestaurantDataStatus
            ? {} // Start fresh if using deprecated data status
            : currentRestaurantDataStatus;

        if (expired.size === 0) {
          return [restaurantUUID, updatedDataStatus];
        }

        for (const [syncType, metadata] of Object.entries(syncData)) {
          const typedSyncType = syncType as OfflineSyncType;
          if (!expired.has(typedSyncType)) {
            continue;
          }

          try {
            const updatedSyncType = await syncOfflineData(
              metadata,
              restaurantUUID,
              supplierUUID,
            );

            // Build updated data status for each sync type per restaurant
            if (updatedSyncType) {
              updatedDataStatus[updatedSyncType] = {
                downloadedDate: nowISOString,
              };
            }
          } catch (e) {
            captureException(e);
          }
        }

        return [restaurantUUID, updatedDataStatus];
      };

      const results = await Promise.allSettled(chunk.map(syncRestaurant));

      // Update data status per chunk after settling results
      const currentDataStatus = (await dataStatusAdapter.fetch()) ?? {};
      const updatedDataStatus = results.reduce(
        (acc, result) => {
          if (
            result.status === 'fulfilled' &&
            result.value &&
            Array.isArray(result.value)
          ) {
            const [restaurantUUID, restaurantDataStatus] = result.value;
            if (
              restaurantUUID &&
              Object.keys(restaurantDataStatus).length > 0
            ) {
              acc[restaurantUUID] = {
                ...(currentDataStatus[restaurantUUID] ?? {}),
                ...restaurantDataStatus,
              };
            }
          }

          return acc;
        },
        { ...currentDataStatus },
      );

      await dataStatusAdapter.save(updatedDataStatus);
      i += MAX_CONCURRENT_ACCOUNT_DOWNLOADS;
    }
  }
};
